import { labelFormatDistance } from '@racemap/utilities/formattings/units';
import type { UnitType } from '@racemap/utilities/types/units';
import type { FC } from 'react';
import { formatHHMMSS } from '../utils';
import {
  type ChartTheme,
  GC_H,
  GC_INNER_H,
  GC_PAD_LEFT,
  GC_PAD_TOP,
  X_TICK_COUNT,
  Y_TICK_COUNT,
} from './layout';
import type { Scales } from './scales';

const ticks = (count: number) => Array.from({ length: count + 1 }, (_, i) => i / count);

/** Axes, tick marks and axis names. Drawn under the data. */
export const AxesChrome: FC<{ innerWidth: number; theme: ChartTheme; hasData: boolean }> = ({
  innerWidth,
  theme,
  hasData,
}) => (
  <>
    <line
      x1={GC_PAD_LEFT}
      y1={GC_PAD_TOP + GC_INNER_H}
      x2={GC_PAD_LEFT}
      y2={GC_PAD_TOP - 8}
      stroke={theme.axisStroke}
      strokeWidth={1}
      markerEnd="url(#gc-arrow)"
    />
    <line
      x1={GC_PAD_LEFT}
      y1={GC_PAD_TOP + GC_INNER_H}
      x2={GC_PAD_LEFT + innerWidth + 8}
      y2={GC_PAD_TOP + GC_INNER_H}
      stroke={theme.axisStroke}
      strokeWidth={1}
      markerEnd="url(#gc-arrow)"
    />
    {/* Outside the clip path, so they reach into the padding. */}
    {ticks(Y_TICK_COUNT).map((ny) => {
      const py = GC_PAD_TOP + (1 - ny) * GC_INNER_H;
      return (
        <line
          key={ny}
          x1={GC_PAD_LEFT}
          y1={py}
          x2={GC_PAD_LEFT - 4}
          y2={py}
          stroke={theme.tickStroke}
          strokeWidth={1}
        />
      );
    })}
    {ticks(X_TICK_COUNT).map((nx) => {
      const px = GC_PAD_LEFT + nx * innerWidth;
      return (
        <line
          key={nx}
          x1={px}
          y1={GC_PAD_TOP + GC_INNER_H}
          x2={px}
          y2={GC_PAD_TOP + GC_INNER_H + 4}
          stroke={theme.tickStroke}
          strokeWidth={1}
        />
      );
    })}
    <text
      transform={`translate(${GC_PAD_LEFT - 4}, ${GC_PAD_TOP + 26}) rotate(-90)`}
      textAnchor="middle"
      fontSize={9}
      fill={theme.labelFill}
    >
      offset
    </text>
    <text
      x={GC_PAD_LEFT + innerWidth - 14}
      y={GC_H - 42}
      textAnchor="middle"
      fontSize={9}
      fill={theme.labelFill}
    >
      time
    </text>
    {!hasData && (
      <text
        x={GC_PAD_LEFT + innerWidth / 2}
        y={GC_PAD_TOP + GC_INNER_H / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={10}
        fill={theme.noDataFill}
      >
        No data - fetch or hover
      </text>
    )}
  </>
);

/** Offset and time values at the ticks. Drawn over the data. */
export const TickLabels: FC<{
  innerWidth: number;
  theme: ChartTheme;
  scales: Scales;
  unitType: UnitType;
}> = ({ innerWidth, theme, scales, unitType }) => (
  <>
    {ticks(Y_TICK_COUNT).map((ny) => {
      const val = scales.minOffset + ny * (scales.maxOffset - scales.minOffset);
      const pixelY = GC_PAD_TOP + (1 - ny) * GC_INNER_H;
      return (
        <text
          key={ny}
          x={GC_PAD_LEFT - 6}
          y={pixelY + 4}
          textAnchor="end"
          fontSize={8}
          fill={theme.labelFill}
        >
          {labelFormatDistance(val, unitType, 1)}
        </text>
      );
    })}
    {ticks(X_TICK_COUNT).map((nx) => {
      const ms = scales.minTime + nx * (scales.maxTime - scales.minTime);
      const pixelX = GC_PAD_LEFT + nx * innerWidth;
      const labelY = GC_PAD_TOP + GC_INNER_H + 16;
      return (
        <text
          key={nx}
          x={pixelX}
          y={labelY}
          textAnchor="middle"
          fontSize={8}
          fill={theme.labelFill}
          transform={`rotate(-30, ${pixelX}, ${labelY})`}
        >
          {formatHHMMSS(ms)}
        </text>
      );
    })}
  </>
);
